import type { LandSearchFilters } from '../types/landFilters.types'

// All restriction filter keys for easy reference
export const RESTRICTION_FILTER_KEYS = [
  'protected_building',
  'polluted_zone',
  'reserved_zone',
  'underwater_protected_zone',
  'forest_zone',
  'forest_limit_zone',
  'watercourse_limit_zone',
  'built_sites_protected_zone',
  'nature_protection_zone',
  'archaeological_zone',
  'danger_zone',
  'mandatory_planning_zone',
  'building_right',
  'protected_objects',
  'no_max_cos_zone',
  'not_yet_defined_special_zone',
  'other_zone',
] as const

/**
 * Default filter states for land filters
 * Applied when no active filters are set and no URL parameters override them
 */
export const DEFAULT_LAND_FILTERS: Partial<LandSearchFilters> = {
  // Zone and land display settings
  // specialZone: undefined, // No special zone filter by default
  no_building_zone: 'hide', // No building zones hidden by default
  // bare_land: undefined, // No bare land filter by default

  // Owners type filter
  // owners_type: undefined, // No owners type filter by default

  // Restriction filters - all displayed by default
  protected_building: 'display',
  polluted_zone: 'display',
  reserved_zone: 'display',
  underwater_protected_zone: 'display',
  forest_zone: 'display',
  forest_limit_zone: 'display',
  watercourse_limit_zone: 'display',
  built_sites_protected_zone: 'display',
  nature_protection_zone: 'display',
  archaeological_zone: 'display',
  danger_zone: 'display',
  mandatory_planning_zone: 'display',
  building_right: 'display',
  protected_objects: 'display',
  no_max_cos_zone: 'display',
  not_yet_defined_special_zone: 'display',
  other_zone: 'display',

  building_permits_classification: [], // No classification filters by default
}

/**
 * Checks if any meaningful filters are currently active
 * Excludes default display modes from counting as "active"
 */
export const hasActiveFilters = (filters: LandSearchFilters): boolean => {
  return Object.entries(filters).some(([key, value]) => {
    // Skip mode from active filter count
    if (key === 'mode') return false

    // Check range filters (min/max objects)
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      if ('min' in value || 'max' in value) {
        return value.min !== undefined || value.max !== undefined
      }
    }

    // Check array filters
    if (Array.isArray(value)) {
      return value.length > 0
    }

    // Check string filters (non-empty strings)
    if (typeof value === 'string') {
      return value !== ''
    }

    // Check display/hide filters - only count non-default values as active
    if (key === 'specialZone' && value) return true
    if (key === 'no_building_zone' && value) return true
    if (key === 'bare_land' && value) return true
    if (key === 'owners_type' && value) return true

    // Check all restriction filters - count as active if not 'display'
    if (RESTRICTION_FILTER_KEYS.includes(key as any) && value !== 'display') {
      return true
    }

    // Check boolean/number filters
    if (typeof value === 'boolean' || typeof value === 'number') {
      return true
    }

    return false
  })
}

/**
 * Checks if any URL parameters contain filter-related data
 */
export const hasUrlFilterParams = (urlParams: URLSearchParams): boolean => {
  const filterKeys = [
    'location_search',
    'location_type',
    'municipality',
    'district',
    'region',
    'npa',
    'plan',
    'area_min',
    'area_max',
    'ownership',
    'owners_type',
    'development_score_min',
    'development_score_max',
    'under_exploited_score_min',
    'under_exploited_score_max',
    'building_area_min',
    'building_area_max',
    'building_year_min',
    'building_year_max',
    'renovation_year_min',
    'renovation_year_max',
    'floor_count_min',
    'floor_count_max',
    'building_height_min',
    'building_height_max',
    'transaction_date_min',
    'transaction_date_max',
    'purchase_price_min',
    'purchase_price_max',
    'rent_price_min',
    'rent_price_max',
    'special_zone',
    'no_building_zone',
    'bare_land',
    // All restriction filter keys
    ...RESTRICTION_FILTER_KEYS,
    'owners_min',
    'owners_max',
  ]

  return filterKeys.some((key) => urlParams.has(key))
}

/**
 * Applies default filters when conditions are met:
 * - No active filters are currently set
 * - No URL parameters contain filter data
 */
export const applyDefaultFiltersIfNeeded = (
  currentFilters: LandSearchFilters,
  urlParams: URLSearchParams
): LandSearchFilters => {
  // Check if we should apply defaults
  const hasActive = hasActiveFilters(currentFilters)
  const hasUrlParams = hasUrlFilterParams(urlParams)

  // Only apply defaults if no active filters and no URL filter parameters
  if (!hasActive && !hasUrlParams) {
    return {
      ...currentFilters,
      ...DEFAULT_LAND_FILTERS,
    }
  }

  // Return current filters unchanged if conditions not met
  return currentFilters
}
